import { useState } from "react";
import { CalendarClock, FileSignature, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";
import api from "../lib/api";
import { CLIENT_TYPES, SERVICES } from "../data/content";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

function Success({ reference, onReset, label }) {
  return (
    <div data-testid="form-success" className="rounded-3xl border border-gold/25 bg-navy-elevated p-10 text-center">
      <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.3} />
      <h3 className="mt-5 font-serif text-3xl text-white">Demande transmise</h3>
      <p className="mt-3 text-slate-300">{label}</p>
      <p className="mt-4 inline-block rounded-full border border-gold/40 px-4 py-2 font-mono text-sm text-gold" data-testid="success-reference">
        Réf. {reference}
      </p>
      <button onClick={onReset} className="mt-8 block w-full rounded-full border border-white/15 px-6 py-3 text-sm text-white hover:border-gold/50">
        Nouvelle demande
      </button>
    </div>
  );
}

export default function Souscription() {
  const [tab, setTab] = useState("subscription");

  return (
    <div data-testid="souscription-page">
      <Seo title="Souscription & rendez-vous" path="/souscription" description="Demandez votre abonnement ou un rendez-vous Speed Détresse. Chaque demande crée un dossier suivi." />
      <PageHero
        overline="Souscription & rendez-vous"
        title={["Rejoignez", "Speed Détresse"]}
        subtitle="Chaque demande crée automatiquement un dossier suivi dans l'application, avec sa référence unique."
        testid="souscription-hero"
      />

      <section className="relative z-10 mx-auto max-w-4xl px-5 py-14 lg:px-8">
        <div className="mb-10 flex gap-3">
          <button data-testid="tab-subscription" onClick={() => setTab("subscription")}
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${tab === "subscription" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <FileSignature className="h-4 w-4" /> Abonnement
          </button>
          <button data-testid="tab-appointment" onClick={() => setTab("appointment")}
            className={`flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium transition-colors ${tab === "appointment" ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:text-gold"}`}>
            <CalendarClock className="h-4 w-4" /> Rendez-vous
          </button>
        </div>
        <Reveal>{tab === "subscription" ? <SubscriptionForm /> : <AppointmentForm />}</Reveal>
      </section>
    </div>
  );
}

function SubscriptionForm() {
  const empty = { client_type: CLIENT_TYPES[0], nom: "", email: "", telephone: "", adresse: "", logements: 1, surface_moyenne: "", types_biens: "", services: [], creneau: "", message: "", consentement: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const toggleService = (s) =>
    setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try {
      const r = await api.subscription({
        ...f,
        logements: Number(f.logements) || 1,
        surface_moyenne: f.surface_moyenne ? Number(f.surface_moyenne) : null,
        types_biens: f.types_biens ? f.types_biens.split(",").map((x) => x.trim()) : [],
        hp,
      });
      setDone(r.reference);
    } catch (err) {
      toast.error("Une erreur est survenue.");
    } finally { setBusy(false); }
  };

  if (done) return <Success reference={done} onReset={() => { setDone(null); setF(empty); }} label="Votre demande d'abonnement a été enregistrée." />;

  return (
    <form onSubmit={submit} data-testid="subscription-form" className="space-y-5 rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
      <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div>
        <label className="mb-2 block text-xs uppercase tracking-wide text-slate-400">Type de client</label>
        <select data-testid="sub-client-type" value={f.client_type} onChange={(e) => setF({ ...f, client_type: e.target.value })} className={inputCls}>
          {CLIENT_TYPES.map((c) => <option key={c} value={c} className="bg-navy">{c}</option>)}
        </select>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <input required data-testid="sub-nom" placeholder="Nom / prénom ou société" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={inputCls} />
        <input required data-testid="sub-telephone" placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className={inputCls} />
        <input required type="email" data-testid="sub-email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
        <input data-testid="sub-adresse" placeholder="Adresse" value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} className={inputCls} />
        <input type="number" min="1" data-testid="sub-logements" placeholder="Nombre de logements" value={f.logements} onChange={(e) => setF({ ...f, logements: e.target.value })} className={inputCls} />
        <input type="number" data-testid="sub-surface" placeholder="Surface moyenne (m²)" value={f.surface_moyenne} onChange={(e) => setF({ ...f, surface_moyenne: e.target.value })} className={inputCls} />
      </div>
      <input data-testid="sub-types" placeholder="Types de biens (ex : T2, maison…)" value={f.types_biens} onChange={(e) => setF({ ...f, types_biens: e.target.value })} className={inputCls} />
      <div>
        <label className="mb-2 block text-xs uppercase tracking-wide text-slate-400">Services souhaités</label>
        <div className="flex flex-wrap gap-2">
          {SERVICES.map((s) => (
            <button type="button" key={s} data-testid={`sub-service-${s}`} onClick={() => toggleService(s)}
              className={`rounded-full px-4 py-2 text-xs transition-colors ${f.services.includes(s) ? "bg-gold text-navy" : "border border-white/15 text-slate-300 hover:border-gold/50"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <input data-testid="sub-creneau" placeholder="Créneau souhaité (ex : mardi matin)" value={f.creneau} onChange={(e) => setF({ ...f, creneau: e.target.value })} className={inputCls} />
      <textarea rows="3" data-testid="sub-message" placeholder="Message complémentaire" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} />
      <label className="flex items-start gap-3 text-xs text-slate-400">
        <input type="checkbox" data-testid="sub-consent" checked={f.consentement} onChange={(e) => setF({ ...f, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
        J'accepte que mes données soient traitées pour ma demande d'abonnement.
      </label>
      <button type="submit" disabled={busy} data-testid="sub-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Envoyer ma demande d'abonnement <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}

function AppointmentForm() {
  const empty = { nom: "", email: "", telephone: "", espace: "proprietaire", adresse: "", creneau: "", message: "", consentement: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try {
      const r = await api.appointment({ ...f, hp });
      setDone(r.reference);
    } catch (err) { toast.error("Une erreur est survenue."); } finally { setBusy(false); }
  };

  if (done) return <Success reference={done} onReset={() => { setDone(null); setF(empty); }} label="Votre demande de rendez-vous a été enregistrée." />;

  return (
    <form onSubmit={submit} data-testid="appointment-form" className="space-y-5 rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
      <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="grid gap-4 sm:grid-cols-2">
        <input required data-testid="rdv-nom" placeholder="Nom / prénom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={inputCls} />
        <input required data-testid="rdv-telephone" placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className={inputCls} />
        <input required type="email" data-testid="rdv-email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
        <select data-testid="rdv-espace" value={f.espace} onChange={(e) => setF({ ...f, espace: e.target.value })} className={inputCls}>
          <option value="proprietaire" className="bg-navy">Propriétaire</option>
          <option value="locataire" className="bg-navy">Locataire</option>
          <option value="airbnb" className="bg-navy">Airbnb</option>
          <option value="professionnel" className="bg-navy">Professionnel</option>
        </select>
      </div>
      <input data-testid="rdv-adresse" placeholder="Adresse du logement" value={f.adresse} onChange={(e) => setF({ ...f, adresse: e.target.value })} className={inputCls} />
      <input data-testid="rdv-creneau" placeholder="Créneau souhaité" value={f.creneau} onChange={(e) => setF({ ...f, creneau: e.target.value })} className={inputCls} />
      <textarea rows="3" data-testid="rdv-message" placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} />
      <label className="flex items-start gap-3 text-xs text-slate-400">
        <input type="checkbox" data-testid="rdv-consent" checked={f.consentement} onChange={(e) => setF({ ...f, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
        J'accepte que mes données soient traitées pour ma demande de rendez-vous.
      </label>
      <button type="submit" disabled={busy} data-testid="rdv-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Demander un rendez-vous <ArrowRight className="h-4 w-4" /></>}
      </button>
    </form>
  );
}
